import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { useState } from 'react';
import DashboardsList from '@Components/Dashboards/List';
import DashboardForm from '@Components/Dashboards/Form';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import type { Dashboard } from '@Types/dashboard';

const CREATE_FORM_ID = 'create-dashboard-form';
const EDIT_FORM_ID = 'edit-dashboard-form';

export default function Dashboards() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDashboard, setEditDashboard] = useState<Dashboard | null>(null);

  const handleEditClick = (row: { original: Dashboard }) => {
    setEditDashboard(row.original);
    setEditOpen(true);
  };

  const handleCreateClick = () => {
    setCreateOpen(true);
  };

  return (
    <div>
      <Breadcrumbs navList={[{ name: 'Dashboards', href: '/dashboards' }]} />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Dashboards</Heading>
        </div>

        {/* Create Sheet */}
        <Sheet open={createOpen} onOpenChange={setCreateOpen}>
          <SheetContent side="right" className="w-full sm:max-w-xl">
            <SheetHeader>
              <SheetTitle>Create dashboard</SheetTitle>
              <SheetDescription>
                Add a new dashboard with a name, description and resource.
              </SheetDescription>
            </SheetHeader>

            <SheetBody className="grow">
              <DashboardForm
                onSuccess={() => setCreateOpen(false)}
                submitLabel="Create"
                formId={CREATE_FORM_ID}
                hideInternalActions
              />
            </SheetBody>

            <SheetFooter className="mt-4">
              <Button
                type="submit"
                form={CREATE_FORM_ID}
                variant="primary"
                className="grow"
              >
                Create
              </Button>
              <SheetClose asChild>
                <Button variant="secondary" type="button" className="grow">
                  Cancel
                </Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <DashboardsList
        onEditClick={handleEditClick}
        onCreateClick={handleCreateClick}
      />

      {/* Edit Sheet */}
      <Sheet
        open={editOpen}
        onOpenChange={(isOpen) => {
          setEditOpen(isOpen);
          if (!isOpen) setEditDashboard(null);
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Edit dashboard</SheetTitle>
            <SheetDescription>
              Update the dashboard name, description and resource.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="grow">
            {editDashboard && (
              <DashboardForm
                key={editDashboard.id}
                editId={editDashboard.id}
                defaultValues={{
                  name: editDashboard.name ?? '',
                  description: editDashboard.description ?? '',
                  embeddableResource: editDashboard.embeddableResource ?? '',
                }}
                onSuccess={() => {
                  setEditOpen(false);
                  setEditDashboard(null);
                }}
                submitLabel="Update"
                formId={EDIT_FORM_ID}
                hideInternalActions
              />
            )}
          </SheetBody>

          <SheetFooter className="mt-4">
            <Button
              type="submit"
              form={EDIT_FORM_ID}
              variant="primary"
              className="grow"
            >
              Update
            </Button>
            <SheetClose asChild>
              <Button variant="secondary" type="button" className="grow">
                Cancel
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
